import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { AgentView } from '@lemoncloud/ssocio-chatbots-api';
import { QueryClient } from '@tanstack/react-query';
import { AgentFormBasicRef } from '@web/app/features/agents/components';

import { agentsKeys } from '@codes/agents';
import { AgentFormData, formDataToCreateDTO, formDataToUpdateDTO } from '@codes/agents';

import { updateCacheWithNewAgent, updateCacheWithUpdatedAgent } from './cache-utils';

export const createPromptsIfNeeded = async (
    advancedFormRef: React.RefObject<AgentFormBasicRef>,
    createPromptMutation: any,
    queryClient: QueryClient
): Promise<{ promptId?: string; userPromptId?: string }> => {
    if (!advancedFormRef.current) {
        return {};
    }

    const promptCreationData = advancedFormRef.current.getPromptCreationData();
    const updates: { promptId?: string; userPromptId?: string } = {};

    // Create system prompt if needed
    if (promptCreationData.systemPrompt) {
        const createdSystemPrompt = await createPromptMutation.mutateAsync(promptCreationData.systemPrompt);
        updates.promptId = createdSystemPrompt.id;
        updateCacheWithNewAgent(queryClient, 'prompts', createdSystemPrompt);
    }

    // Create user prompt if needed
    if (promptCreationData.userPrompt) {
        const createdUserPrompt = await createPromptMutation.mutateAsync(promptCreationData.userPrompt);
        updates.userPromptId = createdUserPrompt.id;
        updateCacheWithNewAgent(queryClient, 'prompts', createdUserPrompt);
    }

    return updates;
};

export const requestCreateAgent = async (
    formData: AgentFormData,
    createMutation: any,
    queryClient: QueryClient,
    t: any
): Promise<AgentView> => {
    const dto = formDataToCreateDTO(formData);
    const createdAgent = await createMutation.mutateAsync(dto);

    toast({
        title: t('agent.form.messages.createSuccess'),
    });

    updateCacheWithNewAgent(queryClient, 'agents', createdAgent);
    return createdAgent;
};

export const requestUpdateAgent = async (
    agentId: string,
    formData: AgentFormData,
    updateMutation: any,
    queryClient: any,
    t: any
): Promise<AgentView> => {
    const dto = formDataToUpdateDTO(agentId, formData);
    const updatedAgent = await updateMutation.mutateAsync(dto);

    toast({
        title: t('agent.form.messages.updateSuccess'),
    });

    queryClient.setQueryData(agentsKeys.detail(updatedAgent.id), updatedAgent);
    updateCacheWithUpdatedAgent(queryClient, 'agents', updatedAgent, agentId);
    return updatedAgent;
};
